import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { AlignmentTypeahead } from './AlignmentTypeahead';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/state/auth/AuthProvider';
import { useLanguage } from '@/state/language/LanguageProvider';
import api from '@/services/api';
import { ProjectModeToggle, type ProjectPageMode } from '@/components/transformations/ProjectModeToggle';
import { useAlignmentRoadmapScope } from './AlignmentRoadmapScopeContext';
import { 
  FolderIcon,
  LinkIcon,
  LinkSlashIcon
} from '@heroicons/react/24/outline';

interface Strategy {
  id: string;
  name: string;
}

interface StrategyLink {
  strategy_id: string;
  strategy_name: string;
}

interface RoadmapProject {
  id: string;
  name: string;
  description?: string;
  status: string;
  hierarchy_type?: string;
  parent_id?: string;
  roadmap_id: string;
  /** All strategies this project is linked to (explicit links only) */
  linked_strategies: StrategyLink[];
  /** First linked strategy for backward compat / display when single */
  linked_strategy_id?: string;
  linked_strategy_name?: string;
  depth?: number;
  has_explicit_strategy_link?: boolean;
}

interface RoadmapWithProjects {
  id: string;
  name: string;
  status: string;
  projects: RoadmapProject[];
  programsCount: number;
  projectsCount: number;
}

export const ProjectsManagement: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const openProject = (projectId: string, mode: ProjectPageMode) => {
    navigate(`/transformations/${projectId}`, { state: { viewMode: mode === 'view' } });
  };
  const { scope, scopeKey } = useAlignmentRoadmapScope();
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [roadmapsWithProjects, setRoadmapsWithProjects] = useState<RoadmapWithProjects[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [linkingProjectId, setLinkingProjectId] = useState<string | null>(null);

  useEffect(() => {
    setLinkingProjectId(null);
  }, [scopeKey]);

  useEffect(() => {
    if (!user?.tenantId) {
      return;
    }
    // Projects load independently of strategies so an empty vision path cannot blank the tab.
    void fetchRoadmapsWithProjects();
  }, [user?.tenantId, scopeKey]);

  useEffect(() => {
    if (!user?.tenantId) {
      return;
    }
    void fetchStrategies();
  }, [user?.tenantId, scopeKey]);

  useEffect(() => {
    if (user?.tenantId && strategies.length > 0) {
      void fetchRoadmapsWithProjects();
    }
  }, [strategies.length]);

  const fetchStrategies = async () => {
    try {
      // Header pick filters strategies. "All roadmaps" loads the tenant set.
      const roadmapIds =
        scope.mode === 'selected' && scope.roadmapIds.length > 0
          ? scope.roadmapIds
          : [];
      const response = await api.get(
        `/alignment/strategies/${user?.tenantId}`,
        roadmapIds.length > 0 ? { params: { roadmapIds: roadmapIds.join(',') } } : {}
      );
      if (response.data.success) {
        setStrategies(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch strategies:', err);
    }
  };

  const fetchRoadmapsWithProjects = async () => {
    if (!user?.tenantId) return;
    
    setLoading(true);
    try {
      // Tenant list defaults to 50 rows — that drops most roadmaps. When a specific
      // roadmap is selected, ask for that roadmap_id so its projects actually come back.
      const selectedIds = scope.mode === 'selected' ? scope.roadmapIds : [];
      const transformationQueries =
        selectedIds.length > 0
          ? selectedIds.map((roadmapId) =>
              api.get('/transformations', { params: { roadmap_id: roadmapId, limit: 2000 } })
            )
          : [api.get('/transformations', { params: { limit: 2000 } })];
      const transformationResponses = await Promise.all(transformationQueries);
      const allTransformations: any[] = transformationResponses
        .flatMap((response) => response.data.data || [])
        .filter((t: any) => !t.deleted_at);

      // Fetch all roadmaps and filter to only ACTIVE roadmaps
      const roadmapsResponse = await api.get('/tenant/roadmaps');
      const allRoadmaps = roadmapsResponse.data.data || [];
      const activeRoadmaps = allRoadmaps.filter((r: any) => r.status === 'ACTIVE');
      const roadmapMap = new Map(activeRoadmaps.map((r: any) => [r.id, r]));

      // Group transformations by roadmap_id - ONLY include transformations from ACTIVE roadmaps
      const transformationsByRoadmap = new Map<string, any[]>();
      const unlinkedTransformations: any[] = [];

      allTransformations.forEach((transformation: any) => {
        if (transformation.roadmap_id && roadmapMap.has(transformation.roadmap_id)) {
          // Only include if roadmap is ACTIVE
          if (!transformationsByRoadmap.has(transformation.roadmap_id)) {
            transformationsByRoadmap.set(transformation.roadmap_id, []);
          }
          transformationsByRoadmap.get(transformation.roadmap_id)!.push(transformation);
        } else if (!transformation.roadmap_id) {
          // Transformation not linked to any roadmap - include as unlinked
          unlinkedTransformations.push(transformation);
        }
        // If transformation.roadmap_id exists but roadmap is inactive, exclude it completely
      });

      // Fetch project-strategy links for all transformations
      let projectStrategyLinks: any[] = [];
      if (allTransformations.length > 0 && strategies.length > 0) {
        try {
          // Fetch links for all strategies
          const allLinksPromises = strategies.map(strategy => 
            api.get(`/alignment/project-strategy-links/strategy/${strategy.id}`)
          );
          const allLinksResponses = await Promise.all(allLinksPromises);
          projectStrategyLinks = allLinksResponses.flatMap(response => response.data.data || []);
        } catch (err) {
          console.error('Failed to fetch project-strategy links:', err);
        }
      }

      // Map project_id -> array of strategy links (a project can be linked to multiple strategies)
      const projectToStrategiesMap = new Map<string, StrategyLink[]>();
      projectStrategyLinks.forEach((link: any) => {
        const strategy = strategies.find(s => s.id === link.strategy_id);
        const entry: StrategyLink = {
          strategy_id: link.strategy_id,
          strategy_name: strategy?.name || t('alignment.projects.unknownStrategy')
        };
        if (!projectToStrategiesMap.has(link.transformation_id)) {
          projectToStrategiesMap.set(link.transformation_id, []);
        }
        projectToStrategiesMap.get(link.transformation_id)!.push(entry);
      });

      // Helper function to find parent's strategies (for implicit inheritance)
      const getParentStrategies = (projectId: string, projectList: any[]): StrategyLink[] | undefined => {
        const project = projectList.find(p => p.id === projectId);
        if (!project || !project.parent_id) return undefined;
        const parentLinks = projectToStrategiesMap.get(project.parent_id);
        if (parentLinks && parentLinks.length > 0) return parentLinks;
        return getParentStrategies(project.parent_id, projectList);
      };

      const processProjectsWithStrategyInfo = (transformations: any[], roadmapId?: string): RoadmapProject[] => {
        const projects: RoadmapProject[] = transformations.map((t: any) => {
          const explicitLinks = projectToStrategiesMap.get(t.id) || [];
          let linkedStrategies = explicitLinks;
          let hasExplicitLink = explicitLinks.length > 0;
          if (linkedStrategies.length === 0 && t.parent_id) {
            const inherited = getParentStrategies(t.id, transformations);
            linkedStrategies = inherited || [];
            hasExplicitLink = false;
          }
          const first = linkedStrategies[0];
          return {
            id: t.id,
            name: t.name || t.transformation_name || 'Unnamed Project',
            description: t.description || t.transformation_description,
            status: t.status || t.transformation_status || 'ACTIVE',
            hierarchy_type: t.hierarchy_type,
            parent_id: t.parent_id,
            roadmap_id: roadmapId || '',
            linked_strategies: linkedStrategies,
            linked_strategy_id: first?.strategy_id,
            linked_strategy_name: first?.strategy_name,
            has_explicit_strategy_link: hasExplicitLink
          };
        });

        // Calculate depth for each project
        const calculateDepth = (projectId: string, projectList: RoadmapProject[], depth: number = 0, visited: Set<string> = new Set()): number => {
          if (visited.has(projectId)) return depth;
          visited.add(projectId);
          
          const project = projectList.find(p => p.id === projectId);
          if (!project || !project.parent_id) return depth;
          
          return calculateDepth(project.parent_id, projectList, depth + 1, visited);
        };

        // Add depth to each project
        const projectsWithDepth = projects.map(project => ({
          ...project,
          depth: calculateDepth(project.id, projects)
        }));

        // Sort projects to maintain hierarchy order (parents before children)
        const sortProjectsByHierarchy = (projectList: RoadmapProject[] & { depth?: number }[]): (RoadmapProject & { depth: number })[] => {
          const sorted: (RoadmapProject & { depth: number })[] = [];
          const processed = new Set<string>();
          
          const addProjectAndChildren = (project: RoadmapProject & { depth?: number }) => {
            if (processed.has(project.id)) return;
            
            if (project.parent_id) {
              const parent = projectList.find(p => p.id === project.parent_id);
              if (parent && !processed.has(parent.id)) {
                addProjectAndChildren(parent);
              }
            }
            
            sorted.push({ ...project, depth: project.depth || 0 });
            processed.add(project.id);
            
            const children = projectList.filter(p => p.parent_id === project.id);
            children.forEach(child => addProjectAndChildren(child));
          };
          
          const topLevelProjects = projectList.filter(p => !p.parent_id);
          topLevelProjects.forEach(project => addProjectAndChildren(project));
          
          projectList.forEach(project => {
            if (!processed.has(project.id)) {
              sorted.push({ ...project, depth: project.depth || 0 });
              processed.add(project.id);
            }
          });
          
          return sorted;
        };
        
        return sortProjectsByHierarchy(projectsWithDepth);
      };

      // Process transformations grouped by roadmap (only active roadmaps)
      const roadmapsData: RoadmapWithProjects[] = Array.from(transformationsByRoadmap.entries())
        .filter(([roadmapId]) => roadmapMap.has(roadmapId)) // Only process active roadmaps
        .map(([roadmapId, transformations]) => {
          const roadmap = roadmapMap.get(roadmapId);
          if (!roadmap) return null;

          const sortedProjects = processProjectsWithStrategyInfo(transformations, roadmapId);
          
          // Count programs (projects with at least one child) and total projects
          const programs = sortedProjects.filter(p => 
            sortedProjects.some(child => child.parent_id === p.id)
          );

          // TypeScript type assertion: roadmap is guaranteed to exist after null check
          const roadmapData = roadmap as { id: string; name: string; status: string };
          return {
            id: roadmapData.id,
            name: roadmapData.name,
            status: roadmapData.status,
            projects: sortedProjects,
            programsCount: programs.length,
            projectsCount: sortedProjects.length
          };
        })
        .filter((r): r is RoadmapWithProjects => r !== null);

      // Process unlinked transformations (not in any roadmap)
      let unlinkedRoadmapData: RoadmapWithProjects | null = null;
      if (unlinkedTransformations.length > 0) {
        const sortedUnlinkedProjects = processProjectsWithStrategyInfo(unlinkedTransformations);
        const programs = sortedUnlinkedProjects.filter(p => 
          sortedUnlinkedProjects.some(child => child.parent_id === p.id)
        );
        
        unlinkedRoadmapData = {
          id: 'unlinked',
          name: t('alignment.projects.nonRoadmapProjects') || 'Non-Roadmap Projects',
          status: 'ACTIVE',
          projects: sortedUnlinkedProjects,
          programsCount: programs.length,
          projectsCount: sortedUnlinkedProjects.length
        };
      }

      // Combine roadmaps and unlinked projects. Unlinked only when viewing all roadmaps.
      const combinedRoadmaps = unlinkedRoadmapData
        ? [...roadmapsData, unlinkedRoadmapData]
        : roadmapsData;
      const scopedRoadmaps =
        scope.mode === 'selected'
          ? scope.roadmapIds
              .map((id) => {
                const existing = combinedRoadmaps.find((roadmap) => roadmap.id === id);
                if (existing) {
                  return existing;
                }
                const meta = allRoadmaps.find((roadmap: { id: string }) => roadmap.id === id);
                if (!meta) {
                  return null;
                }
                return {
                  id: meta.id,
                  name: meta.name,
                  status: meta.status || 'ACTIVE',
                  projects: [],
                  programsCount: 0,
                  projectsCount: 0
                } as RoadmapWithProjects;
              })
              .filter((roadmap): roadmap is RoadmapWithProjects => roadmap !== null)
          : combinedRoadmaps;

      setRoadmapsWithProjects(scopedRoadmaps);
    } catch (err) {
      console.error('Failed to fetch roadmaps with projects:', err);
      setError(t('alignment.projects.failedToLoadRoadmaps'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddProjectStrategyLink = async (projectId: string, strategyId: string, roadmapId: string) => {
    if (!user?.tenantId || !strategyId) return;
    const scrollPosition = window.scrollY;
    try {
      const roadmap = roadmapsWithProjects.find(r => r.id === roadmapId);
      if (!roadmap) {
        setError(t('alignment.projects.roadmapNotFound'));
        return;
      }
      await api.post('/alignment/project-strategy-links', {
        transformation_id: projectId,
        strategy_id: strategyId,
        tenant_id: user.tenantId,
        contribution_level: 'MEDIUM'
      }).catch(err => {
        if (err.response?.status === 409) return { data: { success: true } };
        throw err;
      });
      await fetchRoadmapsWithProjects();
      await fetchStrategies();
      requestAnimationFrame(() => window.scrollTo(0, scrollPosition));
    } catch (err: any) {
      console.error('Failed to add strategy link:', err);
      setError(err.response?.data?.message || t('alignment.projects.failedToLinkProjectToStrategy'));
    }
  };

  const handleRemoveProjectStrategyLink = async (projectId: string, strategyId: string) => {
    if (!user?.tenantId) return;
    const scrollPosition = window.scrollY;
    try {
      await api.delete(`/alignment/project-strategy-links/${projectId}/${strategyId}/${user.tenantId}`)
        .catch(err => {
          if (err.response?.status === 404) return { data: { success: true } };
          throw err;
        });
      await fetchRoadmapsWithProjects();
      await fetchStrategies();
      requestAnimationFrame(() => window.scrollTo(0, scrollPosition));
    } catch (err: any) {
      console.error('Failed to remove strategy link:', err);
      setError(err.response?.data?.message || t('alignment.projects.failedToUnlinkProject'));
    }
  };

  if (loading) {
    return (
      <Card padding="none">
        <CardContent className="p-3">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const projects = roadmapsWithProjects.flatMap((roadmap) => roadmap.projects);
  const canLinkStrategies = (project: RoadmapProject) =>
    Boolean(project.roadmap_id) && project.roadmap_id !== 'unlinked';

  return (
    <div className="space-y-3">
      {/* Error Display */}
      {error && (
        <div className="rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800">
            <span className="text-sm">⚠️</span>
            <span className="text-sm font-medium">{t('alignment.common.error')}</span>
          </div>
          <p className="text-sm text-red-700 mt-1">{error}</p>
        </div>
      )}

      {projects.length === 0 ? (
        <Card padding="none">
          <CardContent className="p-4 text-center">
            <div className="text-[var(--color-surface-text-secondary)]">
              <FolderIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium mb-2">{t('alignment.projects.noActiveRoadmaps')}</h3>
              <p className="mb-4">{t('alignment.projects.noActiveRoadmapsDescription')}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-2">
            {projects.map((project) => {
              const isChildProject = !!project.parent_id;
              const linkedStrategies = project.linked_strategies ?? [];
              const showValidationError = !isChildProject && linkedStrategies.length === 0;
              const alreadyLinkedIds = new Set(linkedStrategies.map(s => s.strategy_id));
              const isProgram = projects.some(p => p.parent_id === project.id);

              return (
                <Card key={project.id} padding="none" className="hover:shadow-md transition-shadow">
                  <CardContent className="p-3">
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <FolderIcon className={`w-4 h-4 flex-shrink-0 ${isProgram ? 'text-[var(--color-info)]' : 'text-[var(--color-surface-text)]'}`} />
                          <div>
                            <button
                              type="button"
                              onClick={() => openProject(project.id, 'view')}
                              className="text-sm font-semibold text-[var(--color-surface-text)] text-left hover:text-[var(--color-accent-bg)] hover:underline"
                            >
                              {project.name}
                            </button>
                            {isChildProject && (
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline" className="text-blue-600 italic">
                                  {t('alignment.projects.inherited') || '(inherited)'}
                                </Badge>
                              </div>
                            )}
                          </div>
                        </div>

                        {project.description && (
                          <p className="text-sm text-[var(--color-surface-text-secondary)] mb-1">
                            {project.description}
                          </p>
                        )}

                        {/* Linked strategies section (below project, like objectives page) */}
                        <div className="mt-1">
                          <h4 className="text-xs font-medium text-[var(--color-surface-text)] mb-0.5">
                            {t('alignment.projects.linkedStrategies')}
                          </h4>
                          {linkedStrategies.length > 0 ? (
                            <div className="space-y-1">
                              {linkedStrategies.map((s) => (
                                <div key={s.strategy_id} className="flex items-center gap-2 px-2 py-1 rounded text-xs flex-wrap">
                                  <span className="truncate">{s.strategy_name}</span>
                                  {!isChildProject && (
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveProjectStrategyLink(project.id, s.strategy_id)}
                                      className="p-0.5 text-red-600 hover:text-red-700 flex-shrink-0"
                                      title={t('alignment.projects.removeStrategy') || 'Unlink'}
                                      aria-label={t('alignment.projects.removeStrategy') || 'Unlink'}
                                    >
                                      <LinkSlashIcon className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-500 italic px-2">
                              {isChildProject
                                ? (t('alignment.projects.inherited') || '(inherited)')
                                : t('alignment.projects.noStrategiesLinked')}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-1.5 flex-shrink-0 min-w-0">
                        <ProjectModeToggle
                          mode="view"
                          onChange={(mode) => openProject(project.id, mode)}
                        />
                        {!isChildProject && canLinkStrategies(project) && (
                          <>
                            <button
                              type="button"
                              onClick={() =>
                                setLinkingProjectId((current) => (current === project.id ? null : project.id))
                              }
                              className="inline-flex items-center gap-1 text-sm font-medium text-[var(--color-accent-bg)] hover:underline"
                            >
                              <LinkIcon className="w-4 h-4" />
                              {t('alignment.projects.linkStrategies') || 'Link strategies'}
                            </button>
                            {linkingProjectId === project.id && (
                              <AlignmentTypeahead
                                value=""
                                onChange={(id) => {
                                  if (!id) return;
                                  handleAddProjectStrategyLink(project.id, id, project.roadmap_id);
                                  setLinkingProjectId(null);
                                }}
                                options={strategies.map((s) => {
                                  const alreadyLinked = alreadyLinkedIds.has(s.id);
                                  return {
                                    id: s.id,
                                    label: alreadyLinked
                                      ? `${s.name} (${t('alignment.projects.alreadyLinked')})`
                                      : s.name,
                                    disabled: alreadyLinked
                                  };
                                })}
                                placeholder={t('alignment.projects.chooseStrategy') || 'Choose strategy...'}
                                className={`min-w-[160px] max-w-[14rem] ${showValidationError ? '[&_input]:border-red-500' : ''}`}
                              />
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
        </div>
      )}
    </div>
  );
};

